import React from 'react';
import { PdfMagnificationLike } from '../../../utils/FormZoomSettings';
import { FitType } from './IFormInteractionHandler';

export interface IHtmlViewerRef {
    magnification: HtmlMagnification;
}

export class HtmlMagnification implements PdfMagnificationLike {
    private zoomLevel: number = 1;
    private fitType: FitType = null;
    private cachedNaturalWidth = 0;
    private cachedNaturalHeight = 0;
    private readonly zoomSteps = [0.1, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];

    constructor(
        private contentRef: React.RefObject<HTMLDivElement>,
        private wrapperRef: React.RefObject<HTMLDivElement>,
        private viewportRef: React.RefObject<HTMLDivElement>,
        private onZoomChange?: (zoom: number) => void
    ) {}

    zoomIn(): number {
        if (!this.contentRef.current) {
            return this.zoomLevel;
        }

        const currentIndex = this.zoomSteps.findIndex((step) => Math.abs(step - this.zoomLevel) < 0.01);

        if (currentIndex !== -1 && currentIndex < this.zoomSteps.length - 1) {
            this.zoomLevel = this.zoomSteps[currentIndex + 1];
        } else {
            const nextIndex = this.zoomSteps.findIndex((step) => step > this.zoomLevel);
            if (nextIndex !== -1) {
                this.zoomLevel = this.zoomSteps[nextIndex];
            }
        }

        this.fitType = null;
        this.applyZoom();
        return this.zoomLevel;
    }

    zoomOut(): number {
        if (!this.contentRef.current) {
            return this.zoomLevel;
        }

        const currentIndex = this.zoomSteps.findIndex((step) => Math.abs(step - this.zoomLevel) < 0.01);

        if (currentIndex !== -1 && currentIndex > 0) {
            this.zoomLevel = this.zoomSteps[currentIndex - 1];
        } else {
            for (let i = this.zoomSteps.length - 1; i >= 0; i--) {
                if (this.zoomSteps[i] < this.zoomLevel) {
                    this.zoomLevel = this.zoomSteps[i];
                    break;
                }
            }
        }

        this.fitType = null;
        this.applyZoom();
        return this.zoomLevel;
    }

    getCurrentZoom(): number {
        return this.zoomLevel;
    }

    zoomTo(zoomPercentage: number): void {
        if (!this.contentRef.current) {
            return;
        }

        this.zoomLevel = Math.max(0.1, Math.min(4, zoomPercentage / 100));
        this.fitType = null;
        this.applyZoom();
    }

    fitToPage(): void {
        if (!this.contentRef.current || !this.viewportRef.current) {
            return;
        }

        this.fitType = 'fitToPage';
        const viewportWidth = this.viewportRef.current.clientWidth;
        const viewportHeight = this.viewportRef.current.clientHeight;
        const { width: contentWidth, height: contentHeight } = this.measureNaturalSize();

        if (contentWidth > 0 && contentHeight > 0 && viewportWidth > 0 && viewportHeight > 0) {
            this.zoomLevel = Math.min(viewportWidth / contentWidth, viewportHeight / contentHeight);
            this.applyZoom();
        }
    }

    fitToWidth(): void {
        if (!this.contentRef.current || !this.viewportRef.current) {
            return;
        }

        this.fitType = 'fitToWidth';
        const viewportWidth = this.viewportRef.current.clientWidth;
        const { width: contentWidth } = this.measureNaturalSize();

        if (contentWidth > 0 && viewportWidth > 0) {
            this.zoomLevel = viewportWidth / contentWidth;
            this.applyZoom();
        }
    }

    fitToAuto(): void {
        if (!this.contentRef.current) {
            return;
        }

        this.fitType = 'fitToAuto';
        this.zoomLevel = 1;
        this.applyZoom();
    }

    getCurrentFitType(): FitType {
        return this.fitType;
    }

    refresh(): void {
        switch (this.fitType) {
            case 'fitToWidth':
                this.fitToWidth();
                break;
            case 'fitToPage':
                this.fitToPage();
                break;
            default:
                this.applyZoom();
        }
    }

    private measureNaturalSize(): { width: number; height: number } {
        const content = this.contentRef.current;
        const wrapper = this.wrapperRef.current;

        if (!content) {
            return { width: this.cachedNaturalWidth, height: this.cachedNaturalHeight };
        }

        const previousTransform = content.style.transform;
        const previousWrapperWidth = wrapper?.style.width ?? '';
        const previousWrapperHeight = wrapper?.style.height ?? '';

        content.style.transform = 'none';
        if (wrapper) {
            wrapper.style.width = 'auto';
            wrapper.style.height = 'auto';
        }

        const measuredWidth = content.scrollWidth;
        const measuredHeight = content.scrollHeight;

        if (wrapper) {
            wrapper.style.width = previousWrapperWidth;
            wrapper.style.height = previousWrapperHeight;
        }
        content.style.transform = previousTransform;

        if (measuredWidth > 0 && measuredHeight > 0) {
            this.cachedNaturalWidth = measuredWidth;
            this.cachedNaturalHeight = measuredHeight;
        }

        return {
            width: measuredWidth > 0 ? measuredWidth : this.cachedNaturalWidth,
            height: measuredHeight > 0 ? measuredHeight : this.cachedNaturalHeight,
        };
    }

    private applyZoom(): void {
        if (!this.contentRef.current || !this.wrapperRef.current) {
            return;
        }

        const content = this.contentRef.current;
        const wrapper = this.wrapperRef.current;
        const { width: naturalWidth, height: naturalHeight } = this.measureNaturalSize();

        if (naturalWidth <= 0 || naturalHeight <= 0) {
            return;
        }

        content.style.transform = `scale(${this.zoomLevel})`;
        content.style.transformOrigin = 'top left';
        content.style.transition = 'transform 0.2s ease-in-out';

        wrapper.style.width = `${naturalWidth * this.zoomLevel}px`;
        wrapper.style.height = `${naturalHeight * this.zoomLevel}px`;

        if (this.onZoomChange) {
            this.onZoomChange(this.zoomLevel * 100);
        }
    }
}
